import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { LogoMark } from "@/components/LogoMark";

export default function Auth({ onAuth }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | otp
  const [role, setRole] = useState("seller"); // seller | buyer
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [otp, setOtp] = useState({ phone: "", code: "", sent: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const goHome = (user) => {
    onAuth(user);
    const target = user.role === "admin" ? "/admin" : user.role === "buyer" ? "/orders" : "/";
    navigate(target, { replace: true });
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = mode === "register" ? { ...form, role } : form;
      const r = await api.post(`/auth/${mode === "register" ? "register" : "login"}`, payload);
      localStorage.setItem("tv_token", r.data.token);
      toast.success(mode === "register" ? "Welcome to True Vision" : "Signed in");
      goHome(r.data.user);
    } catch (err) {
      const m = formatError(err);
      setError(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post(`/auth/otp/send`, { phone: otp.phone });
      setOtp({ ...otp, sent: true });
      toast.success("OTP sent. Please check your phone.");
    } catch (err) {
      const m = formatError(err);
      setError(m); toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.post(`/auth/otp/verify`, { phone: otp.phone, code: otp.code });
      localStorage.setItem("tv_token", r.data.token);
      toast.success("Phone verified");
      goHome(r.data.user);
    } catch (err) {
      const m = formatError(err);
      setError(m); toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-art">
        <img className="auth-logo" src={"https://customer-assets-lxgj4vgw.emergentagent.net/job_authentic-gi-market/artifacts/enmbd7go_12c0bcfd6bac402a8e10c04e91d9096e.png"} alt="GI Bharat" data-testid="auth-logo" />
        <p className="eyebrow">GI BHARAT · TRUE VISION WELFARE SOCIETY</p>
        <h1>Rooted in craft.<br/><em>Ready for the world.</em></h1>
        <p>Connecting India's GI heritage with the world — one trusted home for authorised users, makers and meaningful products.</p>
        <div className="auth-stamp"><ShieldCheck size={18}/> Verified GI ecosystem</div>
      </div>

      <div className="auth-form">
        <p className="eyebrow">MEMBER ACCESS</p>
        <div className="auth-tabs" role="tablist" data-testid="auth-mode-tabs">
          <button className={mode === "login" ? "active" : ""} onClick={() => { setMode("login"); setError(""); }} data-testid="tab-login">Sign in</button>
          <button className={mode === "register" ? "active" : ""} onClick={() => { setMode("register"); setError(""); }} data-testid="tab-register">Create account</button>
          <button className={mode === "otp" ? "active" : ""} onClick={() => { setMode("otp"); setError(""); }} data-testid="tab-otp">Phone OTP</button>
        </div>

        {mode === "register" && (
          <div className="auth-tabs" style={{ marginBottom: 14 }} data-testid="role-tabs">
            <button type="button" className={role === "seller" ? "active" : ""} onClick={() => setRole("seller")} data-testid="role-seller">Authorised user / seller</button>
            <button type="button" className={role === "buyer" ? "active" : ""} onClick={() => setRole("buyer")} data-testid="role-buyer">Buyer</button>
          </div>
        )}

        {mode !== "otp" && (
          <form onSubmit={submitPassword}>
            <h2>{mode === "register" ? "Create your profile" : "Welcome back"}</h2>
            <p className="muted">{mode === "register" ? "Start your authorised seller journey." : "Continue building your GI presence."}</p>
            {mode === "register" && (
              <>
                <label>Full name<input data-testid="register-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="Your name"/></label>
                <label>Phone number<input data-testid="register-phone-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="For OTP updates"/></label>
              </>
            )}
            <label>Email address<input data-testid="auth-email-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required placeholder="name@organisation.com"/></label>
            <label>Password<input data-testid="auth-password-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required placeholder="At least 6 characters"/></label>
            {error && <div className="error" data-testid="auth-error">{error}</div>}
            <button className="primary-btn" data-testid="auth-submit-button" disabled={busy}>{busy ? "Please wait…" : (mode === "register" ? "Create account" : "Sign in")}<ArrowUpRight size={17}/></button>

            <div className="auth-or"><span>or</span></div>
            <button
              type="button"
              className="google-btn"
              data-testid="google-signin-button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirect = `${window.location.origin}/auth/callback`;
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
              }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continue with Google
            </button>
          </form>
        )}

        {mode === "otp" && (
          <form onSubmit={otp.sent ? verifyOtp : sendOtp}>
            <h2>Sign in with phone</h2>
            <p className="muted">We'll text you a 6-digit code. Twilio must be enabled by the admin for this to work.</p>
            <label>Phone number (with country code)
              <input data-testid="otp-phone-input" value={otp.phone} onChange={e => setOtp({...otp, phone: e.target.value})} required placeholder="+919876543210"/>
            </label>
            {otp.sent && (
              <label>OTP code
                <input data-testid="otp-code-input" value={otp.code} onChange={e => setOtp({...otp, code: e.target.value})} required inputMode="numeric" placeholder="6-digit code"/>
              </label>
            )}
            {error && <div className="error" data-testid="auth-error">{error}</div>}
            <button className="primary-btn" data-testid="otp-submit-button" disabled={busy}>
              {busy ? "Please wait…" : (otp.sent ? "Verify code" : "Send OTP")}<ArrowUpRight size={17}/>
            </button>
            {otp.sent && (
              <button type="button" className="text-btn" data-testid="otp-resend-button" onClick={() => setOtp({...otp, sent: false, code: ""})}>Change number</button>
            )}
          </form>
        )}
      </div>
    </main>
  );
}
